import { useEffect, useRef } from "react";

/* ── THE FRAME GOVERNOR (nothing free-runs) ─────────────────────────────────
   Any CSS animation running at natural speed makes Chrome run the full frame
   pipeline every vsync while the window is FOCUSED — the browser's idle
   throttling only covers unfocused/hidden windows. On the main client's Luna
   mark bench this measured ~80% renderer / ~54% GPU-process CPU and melted
   GPUs under video calls; governing the animations dropped it to ~5%/~5%
   (client PR #1267). The portal runs the same LunaMark plus its own
   continuous Luna motions (thinking shimmer, bouncing dots, ambient blobs,
   pulses), so they all share this governor.

   No infinite animation under a governed root ever free-runs: the governor
   sets each one's playbackRate to 0 and advances its currentTime from ONE
   shared interval — SMOOTH_HZ for fast sweeps and big marks (arc steps get
   too coarse at large radii), BASE_HZ for slow idles — using wall-clock
   deltas, so cycle timing stays true to real time (just quantized ≤ one
   tick). Sharing one interval matters: all governed damage lands in the same
   frames instead of interleaving across vsyncs.

   What stays at native smoothness: FINITE animations (enter/exit one-shots,
   the mark's load-in/asteroid/dust burn-outs, framer-motion reveals — they
   end) and all CSS transitions (every state morph). playbackRate 0 — never
   pause() — so playState stays "running" and every existing driver check
   (suspension collect, crescent holds, ride handoffs) behaves unchanged;
   paused animations are skipped, which is exactly what the holds and rides
   need. The ticker idles while the window is blurred/hidden (no timer
   wakeups behind a call), elements scrolled out of view only get the
   adopt-only freeze sweep, and newly started animations free-run for at most
   one tick before adoption.

   Luna surfaces compose (a governed pulse next to a governed mark on a
   governed backdrop), so overlapping roots are legal: each animation is
   advanced at most once per tick — the first registrant's tier wins. */
const BASE_HZ = 12;
const SMOOTH_HZ = 24;
const TICK_MS = 1000 / SMOOTH_HZ;

type Governed = { smooth: boolean; visible: boolean };
const governed = new Map<HTMLElement, Governed>();
let govTimer: number | undefined;
let govViz: IntersectionObserver | undefined;
let smoothLast = 0;
let baseLast = 0;
let baseParity = 0;
let tickN = 0;

/* dt 0 = adopt-only: an out-of-view element still gets its animations frozen
   (playbackRate 0) — left free-running they would keep forcing vsync frames
   from offscreen — but their clocks simply hold until it scrolls back in. */
const advanceElement = (el: HTMLElement, dt: number, seen: Set<Animation>) => {
  for (const a of el.getAnimations({ subtree: true })) {
    if (seen.has(a)) continue;
    seen.add(a);
    if (!(a instanceof CSSAnimation)) continue;
    if (a.effect?.getTiming().iterations !== Infinity) continue;
    if (a.playState !== "running") continue;
    if (a.playbackRate !== 0) a.playbackRate = 0;
    if (dt !== 0) a.currentTime = Number(a.currentTime ?? 0) + dt;
  }
};

const govTick = () => {
  const now = performance.now();
  const smoothDt = now - smoothLast;
  smoothLast = now;
  /* base-rate elements advance every other tick (SMOOTH_HZ / 2) */
  baseParity ^= 1;
  const baseDue = baseParity === 0;
  const baseDt = now - baseLast;
  if (baseDue) baseLast = now;
  /* out-of-view elements only need the adopt-only sweep about once a second */
  const invisDue = tickN++ % SMOOTH_HZ === 0;
  const seen = new Set<Animation>();
  governed.forEach((g, el) => {
    if (!g.visible) {
      if (invisDue) advanceElement(el, 0, seen);
    } else if (g.smooth) advanceElement(el, smoothDt, seen);
    else if (baseDue) advanceElement(el, baseDt, seen);
  });
};

const govSync = () => {
  const shouldRun =
    governed.size > 0 && document.hasFocus() && !document.hidden;
  if (shouldRun && govTimer === undefined) {
    /* fresh deltas — resuming after blur must not fast-forward the pause */
    smoothLast = baseLast = performance.now();
    govTimer = window.setInterval(govTick, TICK_MS);
  } else if (!shouldRun && govTimer !== undefined) {
    window.clearInterval(govTimer);
    govTimer = undefined;
  }
};

/**
 * Hand every infinite CSS animation under `el` (subtree included) to the
 * shared governor. Returns the release: it restores natural playback, since
 * the element may outlive its governance. Callers must apply the guards
 * themselves (WAAPI presence, prefers-reduced-motion) — use the hook below
 * unless the registration needs bespoke conditions like LunaMark's driver.
 */
export const governMotion = (
  el: HTMLElement,
  smooth: boolean,
): (() => void) => {
  if (governed.size === 0) {
    window.addEventListener("focus", govSync);
    window.addEventListener("blur", govSync);
    document.addEventListener("visibilitychange", govSync);
    if (typeof IntersectionObserver !== "undefined")
      govViz = new IntersectionObserver((entries) => {
        for (const e of entries) {
          const g = governed.get(e.target as HTMLElement);
          if (g) g.visible = e.isIntersecting;
        }
      });
  }
  governed.set(el, { smooth, visible: true });
  govViz?.observe(el);
  govSync();
  return () => {
    governed.delete(el);
    govViz?.unobserve(el);
    for (const a of el.getAnimations({ subtree: true }))
      if (a instanceof CSSAnimation && a.playbackRate === 0) a.playbackRate = 1;
    if (governed.size === 0) {
      window.removeEventListener("focus", govSync);
      window.removeEventListener("blur", govSync);
      document.removeEventListener("visibilitychange", govSync);
      govViz?.disconnect();
      govViz = undefined;
      govSync();
    }
  };
};

/**
 * Govern every infinite CSS animation under the returned ref's element.
 * `smooth` opts into the 24fps tier — only for fast motion (sweeps, big
 * marks) where 12fps steps read as stutter.
 *
 * Guarded like LunaMark's driver: a non-browser DOM (jsdom) has no WAAPI, so
 * the component simply renders and the governor never engages. Reduced
 * motion also skips registration — governed components must disable their
 * continuous animations under prefers-reduced-motion themselves, so ticking
 * would be a standing polling workload with nothing to advance, for exactly
 * the users who asked for no motion.
 */
export const useGovernedMotion = <T extends HTMLElement>(
  smooth = false,
  enabled = true,
) => {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    if (
      typeof el.getAnimations !== "function" ||
      typeof CSSAnimation === "undefined"
    )
      return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)
      return;
    return governMotion(el, smooth);
  }, [smooth, enabled]);
  return ref;
};
